<template>
  <div class="click-to-edit" :class="classes" :style="styles">
    <div v-if="edit && !readonly" class="form-inline">
        <div class="input-group">
            <component
                :is="inputType.tag"
                v-bind="componentAttrs"
                ref="input"
                name="edit-field"
                v-focus
                class="input form-control"
                :value="valueLocal"
                v-html="valueLocal"
                @keyup.enter="onKeyupEnter"
                @keyup.escape="doCancel()"
                @click.stop>
            </component>
            <div class="btn-group btn-group-xs">
                <button class="btn btn-success edit-confirm" @click.stop="doSubmit($refs.input.value)"><i class="fa fa-check"></i></button>
                <button class="btn btn-danger edit-cancel"  @click.stop="doCancel()"><i class="fa fa-times"></i></button>
            </div>
        </div>
    </div>
    <component
        :is="tag"
        v-else
        class="text"
        ref="text"
        :class="textClasses"
        :data-placeholder="placeholder"
        @mouseenter="e => hoverable && doShowPencil(e, true)"
        @mouseleave="e => hoverable && doShowPencil(e, false)">
            <span v-html="valueLocal"></span>
            <i
                class="fa fa-pencil edit-pencil"
                :class="pencilClasses"
                @click.stop="edit = true">
            </i>
    </component>
  </div>
</template>

<script>
import { omit, keys } from 'lodash';
import MultiClamp from 'multi-clamp';
import linkifyHtml from 'linkifyjs/html';

export default {
    props: {
        value: {
            default: null,
        },
        inputType: {
            type: Object,
            default() {
                return {tag: 'input', type: 'text'};
            }
        },
        tag: {
            type: String,
            default: 'div',
        },
        bordered: {
            type: Boolean,
            default: false,
        },
        fullwidth: {
            type: Boolean,
            default: true,
        },
        maxwidth: {
            type: Number,
            required: false,
        },
        truncate: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            required: false,
        },
        hoverable: {
            type: Boolean,
            default: true,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        clamp: {
            type: [String, Number],
            default: 1,
        },
        parseLinks: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            edit: false,
            valueLocal: this.value,
            showPencil: false,
            multiClamp: null,
            originalValue: null,
        }
    },
    mounted() {
        if (this.truncate) {
            this.$nextTick(() => {
                this.doMultiClampInit();
            });
        }
    },
    computed: {
        componentAttrs() {
            const attrs = Object.assign({}, this.inputType, this.$attrs);
            return omit(attrs, keys(this.$options.props))
        },
        classes() {
            return {
                'is-fullwidth': this.fullwidth,
                'is-text': this.inputType.tag === 'input',
                'is-textarea': this.inputType.tag === 'textarea',
            }
        },
        styles() {
            const styles = {};
            if (this.maxwidth) styles['max-width'] = `${this.maxwidth}px`;
            return styles;
        },
        textClasses() {
            return {
                'has-border': this.bordered,
                'truncate': this.truncate,
                'has-placeholder': this.showPlaceholder && !this.valueLocal,
                'is-readonly': this.readonly,
            }
        },
        pencilClasses() {
            return {
                'pencil-hidden': (this.hoverable && !this.showPencil) || this.readonly,
            }
        },
        showPlaceholder() {
            return this.placeholder && this.placeholder.length > 0;
        },
    },
    watch: {
        value() {
            this.valueLocal = this.value;
        },
        edit: {
            immediate: true,
            handler(newValue) {
                if (!newValue && this.parseLinks && this.valueLocal) {
                    this.originalValue = this.valueLocal;
                    this.valueLocal = linkifyHtml(this.valueLocal, {
                        defaultProtocol: 'https'
                    });
                } else if (newValue && this.parseLinks && this.originalValue) {
                    this.valueLocal = this.originalValue;
                    this.originalValue = null;
                }
            },
        },
    },
    methods: {
        doSubmit(value) {
            this.edit = false;
            this.valueLocal = value;
            this.doMultiClampInit();
            this.$emit('input', this.valueLocal);
        },
        doCancel() {
            this.edit = false;
            this.valueLocal = this.value;
            this.doMultiClampInit();
        },
        doShowPencil(e, show) {
            this.showPencil = show;
        },
        doMultiClampInit() {
            if (this.truncate) {
                this.$nextTick(() => {
                    try {
                        const textSelector = this.$refs.text.querySelector('span');
                        this.multiClamp = new MultiClamp(textSelector, {
                            ellipsis: '...',
                            clamp: this.clamp,
                            disableCssClamp: true,
                        });
                    } catch (error) {

                    }
                });
            }
        },
        onKeyupEnter(e) {
            if (this.inputType.tag === 'text') {
                this.doSubmit(e.target.value);
            }
        },
    },
    directives: {
        focus: {
            inserted(el) {
                el.focus();
                el.select();
            },
        },
    },
}
</script>

<style lang="scss" scoped>
.click-to-edit {
    display: inline-block;

    .text {
        display: flex;
        position: relative;
        flex-direction: row;
        flex-wrap: nowrap;
        align-items: center;
        margin: 0;

        .truncate {
            overflow: hidden;
        }

        &:not(.is-readonly) {
            cursor: pointer;
        }

        &.has-border {
            border: 1px dashed #ccc;
            padding: 6px 12px
        }

        i {
            margin-top: 3px;
            margin-left: 0.5rem;
            font-weight: normal;
        }

        &.has-placeholder {

            &:before {
                display: block;
                position: absolute;
                content: attr(data-placeholder);
                width: calc(100% - 28px);
                height: 100%;
                top: 5px;
                left: 0;
                opacity: 0.8;
                font-style: italic;
            }
        }

        &.has-border.has-placeholder:before {
            left: 5px;
        }
    }

    &.is-fullwidth {
        display: block;
        max-width: 100%;

        .text {
            justify-content: space-between;
        }

        .input-group, .form-control, .form-inline {
            flex-grow: 1;
        }
    }

    .input-group {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;

        input, textarea {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
        }

        textarea {
            resize: none;
        }
    }

    &.is-textarea {

        .input-group {
            flex-direction: column;

            &:nth-child(1) { order: 1; }
            &:nth-child(2) { order: 3; }
            &:nth-child(3) { order: 2; }
        }

        .text.truncate span {
            max-height: 72px;
        }
    }

     &.is-text {
          .text.truncate span {
            max-height: 25px;
            display: inline-block;
            width: 100%;
        }
     }

    .btn-group {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        min-width: 7.5rem;

        button {
            flex-grow: 1;
            margin-left: 0;

            &:first-child {
                border-top-left-radius: 0;
                border-bottom-left-radius: 0;
            }
        }
    }

    .pencil-hidden {
        opacity: 0;
    }
}
</style>
